import WizardServerError from '../../model/WizardServerError';

const ACTION = {
  UPDATE_ANY_KEY: 'UPDATE_ANY_KEY',
  UPDATE_DISPLAY_SAVE_AND_EXIT_MODAL: 'UPDATE_DISPLAY_SAVE_AND_EXIT_MODAL',
  UPDATE_HAS_CONFIRMED_SAVE_AND_EXIT: 'UPDATE_HAS_CONFIRMED_SAVE_AND_EXIT',
  UPDATE_HAS_FIELD_ERROR: 'UPDATE_HAS_FIELD_ERROR',
  UPDATE_IS_IN_MODAL_STATE: 'UPDATE_IS_IN_MODAL_STATE',
  UPDATE_IS_SAVE_COMPLETE: 'UPDATE_IS_SAVE_COMPLETE',
  UPDATE_SERVER_ERROR: 'UPDATE_SERVER_ERROR',
  UPDATE_SHOULD_SAVE_ON_BLUR: 'UPDATE_SHOULD_SAVE_ON_BLUR',
  UPDATE_WIZARD_COMPLETE: 'UPDATE_WIZARD_COMPLETE',
  ADD_FIELD_DATA_ENTRY: 'ADD_FIELD_DATA_ENTRY',
  CLEAR_FIELD_DATA_ENTRY: 'CLEAR_FIELD_DATA_ENTRY',
  DELETE_FIELD_DATA_ENTRY: 'DELETE_FIELD_DATA_ENTRY',
  UPDATE_IS_PREMIUM_PURCHASE_MODAL_OPEN: 'UPDATE_IS_PREMIUM_PURCHASE_MODAL_OPEN',
  UPDATE_IS_SAVE_DISABLED: 'UPDATE_IS_SAVE_DISABLED',
};

const INIT_STATE = {
  /**
   * canAccessScreeningCriteriaInListingFlow
   *
   * determines if the user can access the screening criteria in the listing flow and review step
   */
  canAccessScreeningCriteriaInListingFlow: false,
  /**
   * canAccessSFCostsAndFees
   *
   * determines if the user can access SF costs and fees in the listing flow, relisting, and review step
   */
  canAccessSFCostsAndFees: false,
  displaySaveAndExitModal: false,
  hasConfirmedSaveAndExit: false,
  hasFieldError: false,
  isInModalState: false,
  /**
   * isSaveDisabled
   *
   * disables the modal's save CTA
   * context: Review, PropertyDetailsContainer
   */
  isSaveDisabled: false,
  /**
   * isInModularListingContext
   *
   * helps handle specific logic when in the post activation context
   * this context is shared between ML and PropertyDetailsContainer
   */
  isInModularListingContext: false,
  /**
   * This handles manually saving for the review edit modal state
   *
   * isSaveComplete is set to true when the user clicks the "save" cta from the modal
   * this triggers a state change and saves the changed values
   *
   * isSaveComplete is set to false when the data is successfully saved
   *
   * works with the shouldSaveOnBlur state
   */
  isSaveComplete: false,
  isRentalProtectionNotRightNowChecked: false,
  isWizardComplete: false,
  serverErrorObj: WizardServerError({}),
  shouldSaveOnBlur: true,
  roomForRentFlow: null,
  /**
   * when true this will bypass updating isSaveComplete (closing modal)
   * use this when we want to save and only call the multi endpoint once
   * saving will be handled in another component
   */
  saveOutsideComponent: false,
  /**
   * a map that holds form fields that have successfully been saved
   *
   * used with bypassModalCloseOnSaveAndWizardAlertDisplay
   */
  savedSuccessFields: {},
  dataToBeSavedMap: new Map(),
  isPremiumPurchaseModalOpen: false,
  isInReactivationFlow: false,
  isSavingSpecialOffers: false,
};

const reducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case ACTION.UPDATE_ANY_KEY: {
      const newState = {
        ...state,
        ...payload,
      };

      return newState;
    }

    case ACTION.REMOVE_SERVER_ERROR: {
      const newState = {
        ...state,
        serverErrorObj: WizardServerError({}),
      };

      return newState;
    }
    case ACTION.UPDATE_IS_SAVE_COMPLETE: {
      const { isSaveComplete } = payload;

      return {
        ...state,
        errorMessage: null,
        hasError: false,
        isSaveComplete,
      };
    }
    case ACTION.UPDATE_IS_IN_MODAL_STATE: {
      const { isInModalState } = payload;

      return {
        ...state,
        isInModalState,
      };
    }
    case ACTION.UPDATE_SHOULD_SAVE_ON_BLUR: {
      const { shouldSaveOnBlur } = payload;

      return {
        ...state,
        errorMessage: null,
        hasError: false,
        shouldSaveOnBlur,
      };
    }
    case ACTION.UPDATE_DISPLAY_SAVE_AND_EXIT_MODAL: {
      const { displaySaveAndExitModal } = payload;

      return {
        ...state,
        displaySaveAndExitModal,
      };
    }
    case ACTION.UPDATE_HAS_CONFIRMED_SAVE_AND_EXIT: {
      const { hasConfirmedSaveAndExit } = payload;

      return {
        ...state,
        hasConfirmedSaveAndExit,
      };
    }
    case ACTION.UPDATE_SERVER_ERROR: {
      const { serverErrorObj } = payload;

      return {
        ...state,
        serverErrorObj: {
          ...state.serverErrorObj,
          ...serverErrorObj,
        },
      };
    }
    case ACTION.ADD_FIELD_DATA_ENTRY: {
      const {
        entryObj,
        entryObj: { fieldName },
      } = payload;

      if (state.dataToBeSavedMap.has(fieldName)) {
        state.dataToBeSavedMap.delete(fieldName);
      }

      state.dataToBeSavedMap.set(fieldName, entryObj);

      return state;
    }
    case ACTION.CLEAR_FIELD_DATA_ENTRY: {
      state.dataToBeSavedMap.clear();

      return state;
    }
    case ACTION.DELETE_FIELD_DATA_ENTRY: {
      const { fieldName } = payload;

      state.dataToBeSavedMap.delete(fieldName);

      return {
        ...state,
        dataToBeSavedMap: state.dataToBeSavedMap,
      };
    }
    case ACTION.UPDATE_HAS_FIELD_ERROR: {
      const { hasFieldError } = payload;

      return {
        ...state,
        hasFieldError,
      };
    }
    case ACTION.UPDATE_WIZARD_COMPLETE: {
      const { isWizardComplete } = payload;

      return {
        ...state,
        isWizardComplete,
      };
    }
    case ACTION.UPDATE_IS_PREMIUM_PURCHASE_MODAL_OPEN: {
      const { isPremiumPurchaseModalOpen } = payload;

      return {
        ...state,
        isPremiumPurchaseModalOpen,
      };
    }
    case ACTION.UPDATE_IS_SAVE_DISABLED: {
      const { isSaveDisabled } = payload;

      return {
        ...state,
        isSaveDisabled,
      };
    }
    default: {
      return state;
    }
  }
};

export { ACTION, INIT_STATE };

export default reducer;
